import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import {
  Envelope, ArrowsClockwise, CircleNotch, CaretDown, CaretRight,
  Tray, Trash, Funnel, EnvelopeOpen, Buildings, Keyboard, Rows, X, CheckCircle,
  Lightning, ShieldCheck, Warning, Sparkle,
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CATEGORY_STYLES,
  REVIEW_STATUS_STYLES,
  URGENCY_COLORS,
  InboxDetailPanel,
  IntentIconBadge,
} from './InboxPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLLAPSE_KEY = 'globalInbox:collapsedProfiles:v1';
const DENSITY_KEY = 'globalInbox:density:v1';

function _loadCollapsed() {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch { return {}; }
}

function _loadDensity() {
  try {
    const v = localStorage.getItem(DENSITY_KEY);
    return v === 'compact' ? 'compact' : 'comfortable';
  } catch { return 'comfortable'; }
}

function ProfileSection({ profile, stats, replies, collapsed, onToggle, onOpenReply, onDelete, selectedReplyId, density }) {
  const accent = profile.color || '#0F172A';
  const hasUnread = (stats?.nieuw || 0) > 0;
  const rowPad = density === 'compact' ? 'py-1' : 'py-2.5';
  const fromMax = density === 'compact' ? 'max-w-[140px]' : 'max-w-[180px]';
  const subjMax = density === 'compact' ? 'max-w-[320px]' : 'max-w-[260px]';
  return (
    <div className="border border-zinc-200 mb-3" data-testid={`global-inbox-profile-${profile.profile_id}`}>
      <button
        type="button"
        onClick={() => onToggle(profile.profile_id)}
        aria-expanded={!collapsed}
        data-testid={`global-inbox-profile-toggle-${profile.profile_id}`}
        className="w-full flex items-center gap-3 px-4 py-3 bg-zinc-50 hover:bg-zinc-100 transition-colors border-l-4 text-left"
        style={{ borderLeftColor: accent }}
      >
        {collapsed ? <CaretRight size={14} className="text-zinc-400" /> : <CaretDown size={14} className="text-zinc-500" />}
        <Buildings size={16} className="text-zinc-500" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading text-sm font-semibold text-zinc-900">{profile.name}</span>
            {!profile.active && (
              <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5">Pauze</span>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
            <span>{stats?.total ?? 0} totaal</span>
            {(stats?.unread_by_mailbox || []).map(m => (
              <span key={m.mailbox} className="text-zinc-400">
                · <span className="font-data">{m.mailbox}</span> ({m.count})
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasUnread && (
            <span
              data-testid={`global-inbox-unread-${profile.profile_id}`}
              className="font-data text-xs font-bold bg-[#0055FF] text-white rounded-full px-2 py-0.5 min-w-[24px] text-center"
            >
              {stats.nieuw > 99 ? '99+' : stats.nieuw}
            </span>
          )}
          {(stats?.wacht_op_goedkeuring || 0) > 0 && (
            <span className="font-data text-[10px] font-bold bg-amber-100 text-amber-800 rounded-sm px-1.5 py-0.5" title="Wachten op review">
              {stats.wacht_op_goedkeuring} review
            </span>
          )}
        </div>
      </button>

      {!collapsed && (
        <div data-testid={`global-inbox-profile-body-${profile.profile_id}`}>
          {(replies || []).length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-zinc-400">
              <Envelope size={20} className="inline mr-2 text-zinc-300" />
              Geen e-mails in deze weergave.
            </div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {replies.map(r => {
                  const cat = CATEGORY_STYLES[r.ai_classification?.category] || CATEGORY_STYLES.other;
                  const rev = REVIEW_STATUS_STYLES[r.review_status] || REVIEW_STATUS_STYLES.nieuw;
                  const isSelected = selectedReplyId === r.reply_id;
                  return (
                    <tr
                      key={r.reply_id}
                      onClick={() => onOpenReply(r)}
                      className={`border-t border-zinc-100 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-zinc-50'} ${r.review_status === 'wacht_op_goedkeuring' ? 'font-medium' : ''}`}
                      data-testid={`global-inbox-row-${r.reply_id}`}
                      ref={(el) => { if (isSelected && el) el.dataset.gibFocused = '1'; }}
                    >
                      <td className={`px-3 ${rowPad} w-6`}>
                        {r.ai_classification?.sentiment === 'positive' && <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
                        {r.ai_classification?.sentiment === 'negative' && <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />}
                        {r.ai_classification?.sentiment === 'neutral' && <span className="w-2 h-2 rounded-full bg-zinc-300 inline-block" />}
                      </td>
                      <td className={`px-3 ${rowPad} w-44`}>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge className={`rounded-sm px-2 py-0.5 text-[10px] font-medium border ${cat.bg}`}>{cat.label}</Badge>
                          {(r.ai_classification?.secondary_intents || []).slice(0, 2).map(si => (
                            <IntentIconBadge key={si} intent={si} />
                          ))}
                        </div>
                      </td>
                      <td className={`px-3 ${rowPad} ${fromMax}`}>
                        <div className="text-sm truncate">{r.from_name || r.from_email?.split('@')[0]}</div>
                        {density !== 'compact' && (
                          <div className="text-[11px] text-zinc-400 truncate">{r.from_email}</div>
                        )}
                      </td>
                      <td className={`px-3 ${rowPad} text-sm ${subjMax} truncate`}>{r.subject || '(geen onderwerp)'}</td>
                      <td className={`px-3 ${rowPad} w-28`}>
                        <span
                          className="text-[10px] text-zinc-500 bg-zinc-100 px-1.5 py-0.5"
                          title={r.received_mailbox || ''}
                        >
                          {r.received_mailbox ? r.received_mailbox.split('@')[0] : '—'}
                        </span>
                      </td>
                      <td className={`px-3 ${rowPad} w-20`}>
                        <span className={`text-xs ${URGENCY_COLORS[r.ai_classification?.urgency] || 'text-zinc-400'}`}>
                          {r.ai_classification?.urgency || '-'}
                        </span>
                      </td>
                      <td className={`px-3 ${rowPad} w-32`}>
                        <Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${rev.bg}`}>{rev.label}</Badge>
                      </td>
                      <td className={`px-3 ${rowPad} text-xs text-zinc-500 w-32 whitespace-nowrap`}>
                        {r.received_at ? new Date(r.received_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className={`px-3 ${rowPad} text-right w-10`}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete(r.reply_id); }}
                          className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors rounded-sm"
                          title="Verwijderen"
                          data-testid={`global-delete-${r.reply_id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Bulk-Approve modal — toont alle "veilige" drafts (categorie question/interest,
 * urgentie low/medium, sentiment positive/neutral) met checkbox per item.
 * Default: alles aangevinkt. User kan zorgvuldig deselecteren voor versturen.
 */
function BulkApproveSafeModal({ open, onClose, onCompleted }) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [criteria, setCriteria] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/email/global-inbox-safe-candidates`, { credentials: 'include' });
      if (!r.ok) throw new Error('load-failed');
      const d = await r.json();
      setCandidates(d.candidates || []);
      setCriteria(d.criteria || null);
      // Default: alles aangevinkt — user moet bewust deselecteren.
      setSelected(new Set((d.candidates || []).map(c => c.reply_id)));
    } catch {
      toast.error('Kon kandidaten niet laden');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) load();
    else {
      setCandidates([]); setSelected(new Set()); setProgress(null); setExpandedRow(null);
    }
  }, [open, load]);

  const toggleOne = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map(c => c.reply_id)));
  };

  const execute = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      toast.warning('Selecteer minimaal 1 e-mail');
      return;
    }
    const phrase = ids.length === 1 ? '1 e-mail' : `${ids.length} e-mails`;
    if (!window.confirm(`Weet je zeker dat je ${phrase} wilt goedkeuren én direct versturen? Deze actie kan niet ongedaan worden gemaakt.`)) return;
    setProcessing(true);
    setProgress({ done: 0, total: ids.length, approved: 0, skipped: 0, failed: 0 });
    // Batched in chunks van 10 om gateway-timeouts te voorkomen.
    const CHUNK = 10;
    let totals = { approved: 0, skipped: 0, failed: 0 };
    let done = 0;
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        const r = await fetch(`${API}/email/global-inbox-bulk-approve`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reply_ids: slice }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          toast.error(`Batch mislukt: ${err.detail || r.statusText}`);
          break;
        }
        const d = await r.json();
        totals.approved += d.approved_count || 0;
        totals.skipped += d.skipped_count || 0;
        totals.failed += d.failed_count || 0;
        done += slice.length;
        setProgress({ done, total: ids.length, ...totals });
      }
      toast.success(`Klaar: ${totals.approved} verzonden${totals.skipped ? ` · ${totals.skipped} overgeslagen` : ''}${totals.failed ? ` · ${totals.failed} mislukt` : ''}`);
      onCompleted?.(totals);
    } catch (e) {
      toast.error(`Verbindingsfout: ${e.message || 'onbekend'}`);
    }
    setProcessing(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4"
      onClick={() => !processing && onClose()}
      data-testid="bulk-approve-modal"
    >
      <div
        className="bg-white w-full max-w-3xl max-h-[88vh] flex flex-col shadow-2xl border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-200 gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
              <ShieldCheck size={18} className="text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-heading text-base font-semibold text-zinc-900">Bulk-approve veilige drafts</h3>
              <p className="text-[12px] text-zinc-500 mt-0.5 leading-snug">
                Selecteer welke AI-concepten je in 1 batch wilt goedkeuren én verzenden. Alleen veilige categorieën worden hier getoond — klachten, offertes en bestellingen blijven handmatig.
              </p>
            </div>
          </div>
          <button
            onClick={() => !processing && onClose()}
            disabled={processing}
            className="p-1.5 hover:bg-zinc-100 disabled:opacity-50"
            data-testid="bulk-approve-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Criteria-strip */}
        {criteria && (
          <div className="px-5 py-2 bg-zinc-50 border-b border-zinc-200 text-[11px] text-zinc-600 flex items-center gap-2 flex-wrap" data-testid="bulk-approve-criteria">
            <Funnel size={12} className="text-zinc-400" />
            <span>Criteria:</span>
            <span>categorie ∈ <strong>{criteria.category.join(', ')}</strong></span>
            <span>·</span>
            <span>urgentie ∈ <strong>{criteria.urgency.join(', ')}</strong></span>
            <span>·</span>
            <span>sentiment ∈ <strong>{criteria.sentiment.join(', ')}</strong></span>
            <span>·</span>
            <span>status = <strong>wacht op review</strong></span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-testid="bulk-approve-list">
          {loading ? (
            <div className="text-center py-12 text-zinc-400">
              <CircleNotch size={24} className="animate-spin inline mr-2" />
              Bezig met laden...
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-16">
              <Warning size={32} className="mx-auto mb-3 text-amber-400" />
              <div className="text-sm text-zinc-600 font-medium">Geen veilige kandidaten gevonden.</div>
              <div className="text-xs text-zinc-400 mt-1.5">Alle drafts die aan de criteria voldoen zijn al verwerkt.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-zinc-200">
                <tr>
                  <th className="text-left px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={selected.size === candidates.length && candidates.length > 0}
                      onChange={toggleAll}
                      data-testid="bulk-approve-select-all"
                      className="cursor-pointer accent-emerald-600"
                      disabled={processing}
                    />
                  </th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Profiel</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Van</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Onderwerp</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Sig.</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map(c => {
                  const isOpen = expandedRow === c.reply_id;
                  return (
                    <Fragment key={c.reply_id}>
                      <tr
                        className={`border-b border-zinc-100 cursor-pointer transition-colors ${selected.has(c.reply_id) ? 'bg-emerald-50/40' : 'hover:bg-zinc-50'}`}
                        onClick={() => toggleOne(c.reply_id)}
                        data-testid={`bulk-approve-row-${c.reply_id}`}
                      >
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(c.reply_id)}
                            onChange={() => toggleOne(c.reply_id)}
                            data-testid={`bulk-approve-toggle-${c.reply_id}`}
                            className="cursor-pointer accent-emerald-600"
                            disabled={processing}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Badge className="rounded-sm px-1.5 py-0 text-[10px] font-medium bg-zinc-100 text-zinc-700 border border-zinc-200">
                            {c.profile_name}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 max-w-[160px]">
                          <div className="text-sm truncate">{c.from_name || c.from_email?.split('@')[0]}</div>
                          <div className="text-[10px] text-zinc-400 truncate">{c.from_email}</div>
                        </td>
                        <td className="px-3 py-2 text-sm max-w-[260px] truncate">{c.subject || '(geen onderwerp)'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] px-1 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-200" title={`category: ${c.category}`}>{c.category}</span>
                            <span className={`text-[10px] px-1 py-0.5 border ${c.sentiment === 'positive' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-zinc-50 text-zinc-600 border-zinc-200'}`} title={`sentiment: ${c.sentiment}`}>{c.sentiment?.[0]?.toUpperCase() || '·'}</span>
                            <span className="text-[10px] px-1 py-0.5 bg-zinc-50 text-zinc-600 border border-zinc-200" title={`urgentie: ${c.urgency}`}>{c.urgency?.toUpperCase() || '·'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setExpandedRow(isOpen ? null : c.reply_id); }}
                            className="text-[11px] text-zinc-500 hover:text-[#0055FF] underline"
                            data-testid={`bulk-approve-preview-toggle-${c.reply_id}`}
                          >
                            {isOpen ? 'Sluit' : 'Toon concept'}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={`${c.reply_id}-preview`} className="bg-zinc-50/60 border-b border-zinc-100">
                          <td colSpan={6} className="px-5 py-3">
                            <div className="text-[11px] text-zinc-500 mb-1">Concept-onderwerp:</div>
                            <div className="text-sm font-medium text-zinc-800 mb-2">{c.draft_subject || '(geen)'}</div>
                            <div className="text-[11px] text-zinc-500 mb-1">Concept-tekst ({c.draft_chars} tekens):</div>
                            <div className="text-xs text-zinc-700 whitespace-pre-wrap bg-white border border-zinc-200 p-2 max-h-48 overflow-y-auto font-data">
                              {c.draft_preview}
                            </div>
                            {c.summary && (
                              <div className="mt-2 text-[11px] text-zinc-500">
                                <strong className="text-zinc-600">AI-samenvatting origineel:</strong> {c.summary}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50/60 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[12px] text-zinc-600">
            {progress ? (
              <span data-testid="bulk-approve-progress">
                Verwerkt {progress.done}/{progress.total} · <span className="text-emerald-700 font-medium">{progress.approved} verzonden</span>
                {progress.skipped > 0 && <> · {progress.skipped} overgeslagen</>}
                {progress.failed > 0 && <> · <span className="text-red-600">{progress.failed} mislukt</span></>}
              </span>
            ) : (
              <span><strong>{selected.size}</strong> van {candidates.length} geselecteerd</span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => !processing && onClose()}
              disabled={processing}
              className="px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 transition-colors border border-zinc-200 disabled:opacity-50"
              data-testid="bulk-approve-cancel"
            >
              Annuleer
            </button>
            <button
              onClick={execute}
              disabled={processing || selected.size === 0 || candidates.length === 0}
              className="px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
              data-testid="bulk-approve-execute"
            >
              {processing ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} weight="fill" />}
              {processing ? 'Verzenden...' : `Goedkeuren & verzenden (${selected.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



export default function GlobalInboxPage() {
  const [stats, setStats] = useState({ profiles: [], grand_total: 0, grand_unread: 0 });
  const [groups, setGroups] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [collapsed, setCollapsed] = useState(() => _loadCollapsed());
  const [autoExpandInitialized, setAutoExpandInitialized] = useState(false);
  const [selectedReply, setSelectedReply] = useState(null);
  const [replyDetail, setReplyDetail] = useState(null);
  const [density, setDensity] = useState(() => _loadDensity());
  const [showHelp, setShowHelp] = useState(false);
  const [showBulkApprove, setShowBulkApprove] = useState(false);
  // arm-then-confirm voor E (approve+send): eerste druk armt; tweede druk binnen 3s verstuurt
  const approveArmRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed)); } catch { /* ignore */ }
  }, [collapsed]);
  useEffect(() => {
    try { localStorage.setItem(DENSITY_KEY, density); } catch { /* ignore */ }
  }, [density]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('unread_only', unreadOnly ? 'true' : 'false');
      params.set('limit', '500');
      const [statsRes, listRes] = await Promise.all([
        fetch(`${API}/email/global-inbox/stats`, { credentials: 'include' }),
        fetch(`${API}/email/global-inbox?${params}`, { credentials: 'include' }),
      ]);
      if (statsRes.ok) {
        const d = await statsRes.json();
        setStats(d);
        // Apply auto-expand the FIRST time we know which profiles have unread:
        // expand profiles with nieuw > 0, collapse the rest. Respect any prior
        // user-chosen state in subsequent fetches.
        if (!autoExpandInitialized) {
          setCollapsed(prev => {
            const stored = Object.keys(prev).length > 0 ? prev : null;
            if (stored) return stored;
            const next = {};
            for (const p of d.profiles || []) {
              next[p.profile_id] = p.nieuw === 0;
            }
            return next;
          });
          setAutoExpandInitialized(true);
        }
      }
      if (listRes.ok) {
        const d = await listRes.json();
        setGroups(d.groups || []);
      }
    } catch (e) {
      toast.error('Kon centrale inbox niet laden');
    }
    setLoading(false);
  }, [unreadOnly, autoExpandInitialized]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll voor verse data elke 60s — komt overeen met sidebar-poll.
  useEffect(() => {
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, [fetchData]);

  const toggleCollapsed = useCallback((profileId) => {
    setCollapsed(prev => ({ ...prev, [profileId]: !prev[profileId] }));
  }, []);

  const repliesByProfile = useMemo(() => {
    const map = {};
    for (const g of groups) map[g.profile_id] = g.replies || [];
    return map;
  }, [groups]);

  // Flat lijst van replies in dezelfde volgorde als ze gerenderd worden:
  // - alleen profielen die UITGEKLAPT zijn (anders kun je toch niets zien)
  // - in dezelfde volgorde als stats.profiles (nieuwste-eerst)
  const visibleReplies = useMemo(() => {
    const out = [];
    for (const p of (stats.profiles || [])) {
      if (collapsed[p.profile_id]) continue;
      const rs = repliesByProfile[p.profile_id] || [];
      for (const r of rs) out.push(r);
    }
    return out;
  }, [stats.profiles, collapsed, repliesByProfile]);

  const visibleIndexById = useMemo(() => {
    const m = new Map();
    visibleReplies.forEach((r, i) => m.set(r.reply_id, i));
    return m;
  }, [visibleReplies]);

  const fetchReplyDetail = (replyId) => {
    fetch(`${API}/email/global-inbox/${replyId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setReplyDetail(d); })
      .catch(() => {});
  };

  const openDetail = (reply) => {
    setSelectedReply(reply.reply_id);
    fetchReplyDetail(reply.reply_id);
  };

  // Selecteer reply op index in visibleReplies; opent detail wanneer mogelijk.
  const selectByIndex = useCallback((idx) => {
    if (visibleReplies.length === 0) return;
    const clamped = ((idx % visibleReplies.length) + visibleReplies.length) % visibleReplies.length;
    const r = visibleReplies[clamped];
    setSelectedReply(r.reply_id);
    fetchReplyDetail(r.reply_id);
    // Scroll de rij in beeld zonder de pagina te jolten.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="global-inbox-row-${r.reply_id}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }, [visibleReplies]);

  const approveAndSendSelected = useCallback(async () => {
    if (!selectedReply) return;
    const detail = replyDetail;
    if (!detail || detail.reply_id !== selectedReply) {
      toast.warning('Wacht even — detail wordt nog geladen');
      return;
    }
    if (!detail.draft_response || !detail.draft_response.body_text) {
      toast.error('Geen concept om goed te keuren');
      return;
    }
    if (detail.review_status === 'verzonden') {
      toast.info('Deze e-mail is al verzonden');
      return;
    }
    // Two-step confirm: eerste E armt 3s, tweede E binnen 3s verstuurt
    if (approveArmRef.current !== selectedReply) {
      approveArmRef.current = selectedReply;
      toast.message('Druk nogmaals E binnen 3s om goed te keuren & versturen', {
        duration: 3000,
      });
      setTimeout(() => {
        if (approveArmRef.current === selectedReply) approveArmRef.current = null;
      }, 3000);
      return;
    }
    approveArmRef.current = null;
    try {
      const res = await fetch(`${API}/email/inbox/${selectedReply}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(`Approve mislukt: ${err.detail || res.statusText}`);
        return;
      }
      toast.success('Goedgekeurd en verzonden');
      const currentIdx = visibleIndexById.get(selectedReply) ?? -1;
      setReplyDetail(null);
      setSelectedReply(null);
      await fetchData();
      // Spring naar volgende reply (vorige index, want huidige verdwijnt uit ongelezen)
      setTimeout(() => {
        if (currentIdx >= 0) selectByIndex(Math.max(0, currentIdx));
      }, 100);
    } catch {
      toast.error('Approve mislukt — netwerkfout');
    }
  }, [selectedReply, replyDetail, fetchData, selectByIndex, visibleIndexById]);

  const rejectSelected = useCallback(async () => {
    if (!selectedReply) return;
    const notes = window.prompt('Optionele reden voor afwijzing (leeg = geen):', '') ?? null;
    if (notes === null) return; // user cancelled
    try {
      const res = await fetch(`${API}/email/inbox/${selectedReply}/reject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(`Afwijzen mislukt: ${err.detail || res.statusText}`);
        return;
      }
      toast.success('Concept afgewezen');
      const currentIdx = visibleIndexById.get(selectedReply) ?? -1;
      setReplyDetail(null);
      setSelectedReply(null);
      await fetchData();
      setTimeout(() => {
        if (currentIdx >= 0) selectByIndex(Math.max(0, currentIdx));
      }, 100);
    } catch {
      toast.error('Afwijzen mislukt — netwerkfout');
    }
  }, [selectedReply, fetchData, selectByIndex, visibleIndexById]);

  // ─── Keyboard shortcuts ──────────────────────────────────────────────
  // J/K = navigeer, Enter = open, Esc = sluit, E = approve+send (2x bevestig),
  // R = reject, D = density toggle, ? = help. Negeer als je in een input/textarea/contenteditable typt.
  useEffect(() => {
    const isTyping = (target) => {
      if (!target) return false;
      const tag = (target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
      if (target.isContentEditable) return true;
      return false;
    };
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTyping(e.target)) return;
      const k = e.key;
      if (k === 'j' || k === 'J') {
        e.preventDefault();
        const cur = selectedReply ? (visibleIndexById.get(selectedReply) ?? -1) : -1;
        selectByIndex(cur + 1);
      } else if (k === 'k' || k === 'K') {
        e.preventDefault();
        const cur = selectedReply ? (visibleIndexById.get(selectedReply) ?? 0) : 0;
        selectByIndex(cur - 1);
      } else if (k === 'Enter') {
        if (selectedReply && !replyDetail) {
          e.preventDefault();
          fetchReplyDetail(selectedReply);
        }
      } else if (k === 'Escape') {
        if (showHelp) { setShowHelp(false); return; }
        if (replyDetail) { setReplyDetail(null); setSelectedReply(null); }
      } else if (k === 'e' || k === 'E') {
        e.preventDefault();
        approveAndSendSelected();
      } else if (k === 'r' || k === 'R') {
        e.preventDefault();
        rejectSelected();
      } else if (k === 'd' || k === 'D') {
        e.preventDefault();
        setDensity(d => d === 'compact' ? 'comfortable' : 'compact');
      } else if (k === '?' || (k === '/' && e.shiftKey)) {
        e.preventDefault();
        setShowHelp(v => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedReply, replyDetail, visibleIndexById, selectByIndex, approveAndSendSelected, rejectSelected, showHelp]);


  const deleteReply = async (replyId) => {
    if (!window.confirm('Weet je zeker dat je deze email permanent wilt verwijderen?')) return;
    try {
      const res = await fetch(`${API}/email/inbox/${replyId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('delete-failed');
      if (selectedReply === replyId) { setReplyDetail(null); setSelectedReply(null); }
      fetchData();
    } catch {
      toast.error('Verwijderen mislukt');
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API}/graph/inbox/sync`, { method: 'POST', credentials: 'include' });
      if (!r.ok) toast.error('Sync mislukt');
      else toast.success('Inbox gesynchroniseerd');
      fetchData();
    } catch {
      toast.error('Sync mislukt');
    }
    setSyncing(false);
  };

  // Genereer ontbrekende AI-concepten voor ALLE mails zonder draft (mode='missing').
  // Werkt in batches van 3 om gateway-timeout te voorkomen, herhaalt tot remaining=0.
  const [generatingMissing, setGeneratingMissing] = useState(false);
  const [missingProgress, setMissingProgress] = useState(null);
  const generateMissingDrafts = async () => {
    if (generatingMissing) return;
    setGeneratingMissing(true);
    let totals = { processed: 0, failed: 0, skipped: 0 };
    try {
      // Eerste call peilt 'remaining'; daarna loop.
      let safety = 50; // max 50 batches * 3 = 150 emails per click — ruim voldoende.
      while (safety-- > 0) {
        const res = await fetch(`${API}/email/inbox/bulk-regenerate`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: 'missing', limit: 3 }),
        });
        if (!res.ok) {
          toast.error('Concept-generatie mislukt');
          break;
        }
        const d = await res.json();
        totals.processed += d.processed || 0;
        totals.failed += d.failed || 0;
        totals.skipped += d.skipped || 0;
        const remaining = d.remaining ?? 0;
        setMissingProgress({ done: totals.processed, remaining });
        if (d.total_considered === 0 || remaining === 0) break;
      }
      toast.success(`Concepten klaar: ${totals.processed} gegenereerd${totals.skipped ? ` · ${totals.skipped} overgeslagen` : ''}${totals.failed ? ` · ${totals.failed} mislukt` : ''}`);
      fetchData();
    } catch (e) {
      toast.error(`Netwerkfout: ${e.message || 'onbekend'}`);
    }
    setGeneratingMissing(false);
    setTimeout(() => setMissingProgress(null), 4000);
  };

  const expandAll = () => {
    setCollapsed(() => {
      const next = {};
      for (const p of stats.profiles || []) next[p.profile_id] = false;
      return next;
    });
  };
  const collapseAll = () => {
    setCollapsed(() => {
      const next = {};
      for (const p of stats.profiles || []) next[p.profile_id] = true;
      return next;
    });
  };

  // Render profiles in the same order as stats (nieuw desc, then alpha).
  const orderedProfiles = stats.profiles || [];

  return (
    <div className="animate-fade-in" data-testid="global-inbox-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <Tray size={24} className="text-[#0055FF]" />
            <h1 className="font-heading text-3xl font-black tracking-tight">Centrale Inbox</h1>
            {stats.grand_unread > 0 && (
              <span
                data-testid="global-inbox-grand-unread"
                className="font-data text-xs font-bold bg-[#0055FF] text-white rounded-full px-2 py-1"
              >
                {stats.grand_unread} ongelezen
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            Alle inboxen van álle profielen op één plek. {orderedProfiles.length} profiel(en) zichtbaar.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="inline-flex border border-zinc-200 rounded-sm overflow-hidden text-xs" data-testid="global-inbox-mode-toggle">
            <button
              onClick={() => setUnreadOnly(true)}
              className={`px-3 py-1.5 flex items-center gap-1.5 ${unreadOnly ? 'bg-[#0055FF] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
              data-testid="global-inbox-mode-unread"
            >
              <Envelope size={12} /> Ongelezen
            </button>
            <button
              onClick={() => setUnreadOnly(false)}
              className={`px-3 py-1.5 flex items-center gap-1.5 ${!unreadOnly ? 'bg-[#0055FF] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
              data-testid="global-inbox-mode-all"
            >
              <EnvelopeOpen size={12} /> Alles
            </button>
          </div>
          <button
            onClick={expandAll}
            className="px-2.5 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 transition-colors border border-zinc-200"
            data-testid="global-inbox-expand-all"
          >
            <CaretDown size={12} className="inline mr-1" /> Klap alles uit
          </button>
          <button
            onClick={collapseAll}
            className="px-2.5 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 transition-colors border border-zinc-200"
            data-testid="global-inbox-collapse-all"
          >
            <CaretRight size={12} className="inline mr-1" /> Klap alles in
          </button>
          <button
            onClick={syncAll}
            disabled={syncing}
            data-testid="global-inbox-sync-btn"
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {syncing ? <CircleNotch size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
            {syncing ? 'Syncen...' : 'Sync inbox'}
          </button>
          <button
            onClick={generateMissingDrafts}
            disabled={generatingMissing}
            data-testid="global-inbox-generate-missing-btn"
            title="Genereer AI-concepten voor alle mails die nog geen antwoord-concept hebben"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 transition-colors"
          >
            {generatingMissing ? <CircleNotch size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
            {generatingMissing
              ? (missingProgress ? `Concepten... ${missingProgress.done}/${missingProgress.done + missingProgress.remaining}` : 'Concepten genereren...')
              : 'Genereer ontbrekende concepten'}
          </button>
          <button
            onClick={() => setShowBulkApprove(true)}
            data-testid="global-inbox-bulk-approve-btn"
            title="Bulk-approve alle veilige drafts (categorie question/interest, urgentie low/medium, sentiment positief/neutraal)"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-[#0055FF] text-white hover:bg-[#0044DD] transition-colors"
          >
            <ShieldCheck size={12} weight="fill" /> Bulk-approve veilige
          </button>
          <button
            onClick={() => setDensity(d => d === 'compact' ? 'comfortable' : 'compact')}
            data-testid="global-inbox-density-toggle"
            title={`Density: ${density} — toggle met D`}
            className={`px-2.5 py-1.5 text-xs border border-zinc-200 transition-colors flex items-center gap-1.5 ${density === 'compact' ? 'bg-zinc-800 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}
          >
            <Rows size={12} /> {density === 'compact' ? 'Compact' : 'Ruim'}
          </button>
          <button
            onClick={() => setShowHelp(true)}
            data-testid="global-inbox-help-btn"
            title="Sneltoetsen (?)"
            className="px-2.5 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 transition-colors border border-zinc-200 flex items-center gap-1.5"
          >
            <Keyboard size={12} /> ?
          </button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="global-inbox-stats">
        {[
          { l: 'Totaal e-mails', v: stats.grand_total, c: 'text-zinc-700' },
          { l: 'Ongelezen', v: stats.grand_unread, c: stats.grand_unread > 0 ? 'text-[#0055FF]' : 'text-zinc-400' },
          { l: 'Profielen', v: orderedProfiles.length, c: 'text-zinc-700' },
          { l: 'Met ongelezen', v: orderedProfiles.filter(p => p.nieuw > 0).length, c: 'text-amber-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white p-3 text-center">
            <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
          </div>
        ))}
      </div>

      {loading && groups.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <CircleNotch size={28} className="animate-spin inline mr-2" />
          Bezig met laden...
        </div>
      ) : orderedProfiles.length === 0 ? (
        <div className="border border-zinc-200 px-4 py-16 text-center text-zinc-400" data-testid="global-inbox-empty">
          <Funnel size={28} className="mx-auto mb-2 text-zinc-300" />
          <div className="text-sm">Je hebt nog geen toegang tot een profiel.</div>
        </div>
      ) : (
        orderedProfiles.map(prof => (
          <ProfileSection
            key={prof.profile_id}
            profile={prof}
            stats={prof}
            replies={repliesByProfile[prof.profile_id] || []}
            collapsed={!!collapsed[prof.profile_id]}
            onToggle={toggleCollapsed}
            onOpenReply={openDetail}
            onDelete={deleteReply}
            selectedReplyId={selectedReply}
            density={density}
          />
        ))
      )}

      {/* Sneltoets-hint onderaan, altijd zichtbaar */}
      <div className="mt-4 text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap" data-testid="global-inbox-shortcut-hint">
        <Keyboard size={12} />
        <span>Sneltoetsen:</span>
        <kbd className="px-1.5 py-0.5 border border-zinc-200 bg-zinc-50 font-data">J/K</kbd>
        <span>navigeer</span>
        <kbd className="px-1.5 py-0.5 border border-zinc-200 bg-zinc-50 font-data">E</kbd>
        <span>approve+send (2×)</span>
        <kbd className="px-1.5 py-0.5 border border-zinc-200 bg-zinc-50 font-data">R</kbd>
        <span>reject</span>
        <kbd className="px-1.5 py-0.5 border border-zinc-200 bg-zinc-50 font-data">D</kbd>
        <span>density</span>
        <kbd className="px-1.5 py-0.5 border border-zinc-200 bg-zinc-50 font-data">?</kbd>
        <span>help</span>
      </div>

      {/* Detail Panel — gebruikt dezelfde panel als InboxPage zodat reply-flow
          identiek blijft. Reply-acties bevatten automatisch het correcte
          profile_id omdat de backend dat uit het reply-doc afleidt. */}
      {replyDetail && (
        <InboxDetailPanel
          reply={replyDetail}
          onClose={() => { setReplyDetail(null); setSelectedReply(null); }}
          onAction={(kind) => {
            if (kind === 'regenerated') {
              fetchData();
              fetch(`${API}/email/global-inbox/${replyDetail.reply_id}`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setReplyDetail(data); })
                .catch(() => {});
              return;
            }
            setReplyDetail(null); setSelectedReply(null); fetchData();
          }}
        />
      )}

      {/* Bulk-approve modal — preview + selectie + execute */}
      <BulkApproveSafeModal
        open={showBulkApprove}
        onClose={() => setShowBulkApprove(false)}
        onCompleted={() => {
          setShowBulkApprove(false);
          fetchData();
        }}
      />

      {/* Help overlay (toggle met "?") */}
      {showHelp && (
        <div
          className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4"
          onClick={() => setShowHelp(false)}
          data-testid="global-inbox-help-overlay"
        >
          <div
            className="bg-white w-full max-w-md shadow-2xl border border-zinc-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <Keyboard size={18} className="text-[#0055FF]" />
                <h3 className="font-heading text-base font-semibold">Sneltoetsen Centrale Inbox</h3>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                className="p-1.5 hover:bg-zinc-100"
                data-testid="global-inbox-help-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-2 text-sm">
              {[
                ['J', 'Volgende e-mail'],
                ['K', 'Vorige e-mail'],
                ['Enter', 'Open geselecteerde e-mail'],
                ['Esc', 'Sluit detail panel / help'],
                ['E', 'Approve concept + verstuur (2× drukken bevestigt)'],
                ['R', 'Reject concept (vraagt om optionele notitie)'],
                ['D', 'Toggle dichtheid (compact / ruim)'],
                ['?', 'Toon / verberg dit overzicht'],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between border-b border-zinc-100 py-1.5 last:border-0">
                  <span className="text-zinc-600">{label}</span>
                  <kbd className="px-2 py-0.5 border border-zinc-300 bg-zinc-50 font-data text-xs">{key}</kbd>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-zinc-100 text-[11px] text-zinc-500 flex items-center gap-2">
              <CheckCircle size={12} className="text-emerald-500" />
              Werkt alleen wanneer je <strong>niet</strong> in een invoerveld typt.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
